import clsx from 'clsx';
import AnimatedLogin from '@/components/Animated/Backgrounds/AnimatedLoginBackground';

const prose = {
  base: 'prose prose-zinc mx-auto',
  spacing: 'px-8 pb-8',
  custom: [
    '[&>h1]:!mb-4',
    '[&>h2]:!mt-8',
    '[&>h3]:!mt-6',
    '[&>p]:text-gray-700',
    '[&>ul]:text-gray-700',
    '[&_a]:text-pink-600',
    '[&_a]:no-underline',
    '[&_a]:hover:text-pink-700'
  ].join(' ')
};

export default function PrivacyPage() {
  return (
    <div className="h-full w-full">
      <div className="relative h-full w-full">
        <AnimatedLogin />
        <div className="h-full w-full overflow-auto">
          <article
            className={clsx(
              prose.base,
              prose.spacing,
              prose.custom,
              'max-w-3xl bg-white px-16 pt-120 pb-100'
            )}
          >
            <h1 className="text-4xl font-bold tracking-tight">Privacy Policy</h1>
            <p className="!mt-2 text-sm text-gray-500 dark:text-gray-400">
              Last Updated: 11/06/2024
            </p>

            <p>
              This Privacy Policy describes Our policies and procedures on the collection, use and
              disclosure of Your information when You use the Service and tells You about Your
              privacy rights and how the law protects You. We use Your Personal data to provide and
              improve the Service. By using the Service, You agree to the collection and use of
              information in accordance with this Privacy Policy.
            </p>

            <h2>Interpretation and Definitions</h2>
            <h3>Interpretation</h3>
            <p>
              The words of which the initial letter is capitalized have meanings defined under the
              following conditions. The following definitions shall have the same meaning regardless
              of whether they appear in singular or in plural.
            </p>

            <h3>Definitions</h3>
            <ul>
              <li>
                <strong>Account</strong> means a unique account created for You to access our
                Service or parts of our Service.
              </li>
              <li>
                <strong>Affiliate</strong> means an entity that controls, is controlled by or is
                under common control with a party, where &quot;control&quot; means ownership of 50%
                or more of the shares, equity interest or other securities entitled to vote for
                election of directors or other managing authority.
              </li>
              <li>
                <strong>Company</strong> (referred to as either &quot;the Company&quot;,
                &quot;We&quot;, &quot;Us&quot; or &quot;Our&quot; in this Agreement) refers to
                Scribl, 999 Main Street #111, Pawtucket, RI 02860.
              </li>
              <li>
                <strong>Cookies</strong> are small files that are placed on Your computer, mobile
                device or any other device by a website, containing the details of Your browsing
                history on that website among its many uses.
              </li>
              <li>
                <strong>Country</strong> refers to: Rhode Island, United States
              </li>
              <li>
                <strong>Device</strong> means any device that can access the Service such as a
                computer, a cellphone or a digital tablet.
              </li>
              <li>
                <strong>Personal Data</strong> is any information that relates to an identified or
                identifiable individual.
              </li>
              <li>
                <strong>Service</strong> refers to the Website.
              </li>
              <li>
                <strong>Service Provider</strong> means any natural or legal person who processes
                the data on behalf of the Company. It refers to third-party companies or individuals
                employed by the Company to facilitate the Service, to provide the Service on behalf
                of the Company, to perform services related to the Service or to assist the Company
                in analyzing how the Service is used.
              </li>
              <li>
                <strong>Usage Data</strong> refers to data collected automatically, either generated
                by the use of the Service or from the Service infrastructure itself (for example,
                the duration of a page visit).
              </li>
              <li>
                <strong>Website</strong> refers to Scribl, accessible from https://scribl.co
              </li>
              <li>
                <strong>You</strong> means the individual accessing or using the Service, or the
                company, or other legal entity on behalf of which such individual is accessing or
                using the Service, as applicable.
              </li>
            </ul>

            <h2>Collecting and Using Your Personal Data</h2>
            <h3>Types of Data Collected</h3>

            <h4>Personal Data</h4>
            <p>
              While using Our Service, We may ask You to provide Us with certain personally
              identifiable information that can be used to contact or identify You. Personally
              identifiable information may include, but is not limited to:
            </p>
            <ul>
              <li>Email address</li>
              <li>First name and last name</li>
              <li>Usage Data</li>
            </ul>

            <h4>Usage Data</h4>
            <p>Usage Data is collected automatically when using the Service.</p>
            <p>
              Usage Data may include information such as Your Device&apos;s Internet Protocol
              address (e.g. IP address), browser type, browser version, the pages of our Service
              that You visit, the time and date of Your visit, the time spent on those pages, unique
              device identifiers and other diagnostic data.
            </p>
            <p>
              When You access the Service by or through a mobile device, We may collect certain
              information automatically, including, but not limited to, the type of mobile device
              You use, Your mobile device unique ID, the IP address of Your mobile device, Your
              mobile operating system, the type of mobile Internet browser You use, unique device
              identifiers and other diagnostic data.
            </p>

            <h3>Tracking Technologies and Cookies</h3>
            <p>
              We use Cookies and similar tracking technologies to track the activity on Our Service
              and store certain information. Tracking technologies used are beacons, tags, and
              scripts to collect and track information and to improve and analyze Our Service.
            </p>

            <h4>Types of Cookies We Use:</h4>
            <ul>
              <li>
                <strong>Necessary / Essential Cookies:</strong> Type: Session Cookies | Purpose:
                These Cookies are essential to provide You with services available through the
                Website and to enable You to use some of its features.
              </li>
              <li>
                <strong>Cookies Policy / Notice Acceptance Cookies:</strong> Type: Persistent
                Cookies | Purpose: These Cookies identify if users have accepted the use of cookies
                on the Website.
              </li>
              <li>
                <strong>Functionality Cookies:</strong> Type: Persistent Cookies | Purpose: These
                Cookies allow us to remember choices You make when You use the Website.
              </li>
            </ul>

            <h2>Use of Your Personal Data</h2>
            <p>The Company may use Personal Data for the following purposes:</p>
            <ul>
              <li>To provide and maintain our Service</li>
              <li>To manage Your Account</li>
              <li>For the performance of a contract</li>
              <li>To contact You</li>
              <li>To provide You with news and updates</li>
              <li>To manage Your requests</li>
              <li>For business transfers</li>
              <li>For other purposes with Your consent</li>
            </ul>

            <h2>Retention of Your Personal Data</h2>
            <p>
              The Company will retain Your Personal Data only for as long as is necessary for the
              purposes set out in this Privacy Policy. We will retain and use Your Personal Data to
              the extent necessary to comply with our legal obligations, resolve disputes, and
              enforce our legal agreements and policies.
            </p>

            <h2>Security of Your Personal Data</h2>
            <p>
              The security of Your Personal Data is important to Us, but remember that no method of
              transmission over the Internet, or method of electronic storage is 100% secure. While
              We strive to use commercially acceptable means to protect Your Personal Data, We
              cannot guarantee its absolute security.
            </p>

            <h2>Children&apos;s Privacy</h2>
            <p>
              Our Service does not address anyone under the age of 13. We do not knowingly collect
              personally identifiable information from anyone under the age of 13.
            </p>

            <h2>Links to Other Websites</h2>
            <p>
              Our Service may contain links to other websites that are not operated by Us. If You
              click on a third party link, You will be directed to that third party&apos;s site. We
              strongly advise You to review the Privacy Policy of every site You visit.
            </p>

            <h2>Changes to this Privacy Policy</h2>
            <p>
              We may update Our Privacy Policy from time to time. We will notify You of any changes
              by posting the new Privacy Policy on this page and update the &quot;Last updated&quot;
              date at the top of this Privacy Policy.
            </p>

            <h2>Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, You can contact us:</p>
            <ul>
              <li>
                By email: <a href="mailto:info@scribl.co">info@scribl.co</a>
              </li>
              <li>
                By visiting this page on our website:{' '}
                <a href="https://scribl.co/contact">https://scribl.co/contact</a>
              </li>
            </ul>
          </article>
        </div>
      </div>
    </div>
  );
}
